import { useEffect, useState, useCallback, useMemo } from 'react';
import { adminGet, adminPost, adminPut, adminDelete, TEST_TIPOS, getTests } from '../../api/adminClient';

const API_BASE = import.meta.env.VITE_API_BASE ?? '';

function ensayoPublicUrl(code) {
  return `${window.location.origin}/secuencias?cod=${encodeURIComponent(code)}`;
}

/** Prefijos de código de tarea (igual que TareaId.cs del sitio viejo). */
const TAREA_PREFIX = {
  libretest: 'LT',
  rotacion: 'R',
  priming: 'P',
  memoriaacortopalabras: 'MCP',
  digitosdirectosinversos: 'DDI',
  aprendizajesuperficialprofundo: 'ASP',
};

function parseTareasCsv(raw) {
  if (!raw?.trim()) return [];
  return raw.toUpperCase().split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean);
}

function joinTareas(list) {
  return list.join(',');
}

function TareasBadges({ tareas, completadas }) {
  const plan = parseTareasCsv(tareas);
  const done = new Set(parseTareasCsv(completadas));
  if (plan.length === 0) {
    return <span className="text-slate-400 text-xs">Sin tareas</span>;
  }
  return (
    <div className="flex flex-wrap gap-1 mt-1.5">
      {plan.map((code) => {
        const ok = done.has(code);
        return (
          <span
            key={code}
            title={ok ? 'Realizada' : 'Pendiente'}
            className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-mono font-medium ${
              ok
                ? 'bg-sky-100 text-sky-700 border border-sky-200'
                : 'bg-slate-100 text-slate-500 border border-slate-200'
            }`}
          >
            {code}
          </span>
        );
      })}
    </div>
  );
}

export default function AdminEnsayos() {
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro]   = useState('');
  const [query, setQuery]     = useState('');
  const [detail, setDetail]   = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [sortDir, setSortDir] = useState(null); // null | 'asc' | 'desc'

  // Modal create / edit
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState('create'); // create | edit
  const [formId, setFormId] = useState(null);
  const [formCode, setFormCode] = useState('');
  const [formSujeto, setFormSujeto] = useState('');
  const [formText, setFormText] = useState('');
  const [formTareas, setFormTareas] = useState('');
  const [formMeta, setFormMeta] = useState(null); // readonly fields on edit
  const [formSaving, setFormSaving] = useState(false);
  const [formCreated, setFormCreated] = useState(null);

  // Picker de tests para armar el CSV
  const [pickerTipo, setPickerTipo] = useState('libretest');
  const [pickerTests, setPickerTests] = useState([]);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [pickerId, setPickerId] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const isId = /^\d+$/.test(query);
      const data = await adminGet(
        !query || isId
          ? '/api/admin/ensayos'
          : `/api/admin/ensayos?sujeto=${encodeURIComponent(query)}`
      );
      setRows(isId ? data.filter((r) => String(r.id).includes(query)) : data);
    } catch (e) {
      alert(e.message);
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!formOpen) return;
    setPickerLoading(true);
    getTests(pickerTipo)
      .then((rows) => setPickerTests(rows.filter((t) => !t.baja)))
      .catch(() => setPickerTests([]))
      .finally(() => setPickerLoading(false));
    setPickerId('');
  }, [formOpen, pickerTipo]);

  const visibleRows = useMemo(() => {
    if (!sortDir) return rows;
    return [...rows].sort((a, b) => {
      const cmp = Number(a.id) - Number(b.id);
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [rows, sortDir]);

  const toggleIdSort = () =>
    setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));

  const openDetail = async (id) => {
    setDetailLoading(true);
    try {
      setDetail(await adminGet(`/api/admin/ensayos/${id}`));
    } catch (e) {
      alert(e.message);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleExport = () => {
    const token = localStorage.getItem('admin_token');
    window.open(`${API_BASE}/api/admin/ensayos/export.csv?_token=${token}`, '_blank');
  };

  const openCreate = () => {
    setFormMode('create');
    setFormId(null);
    setFormCode('(se genera al guardar)');
    setFormSujeto('');
    setFormText('');
    setFormTareas('');
    setFormMeta(null);
    setFormCreated(null);
    setFormOpen(true);
  };

  const openEdit = async (row) => {
    try {
      const full = await adminGet(`/api/admin/ensayos/${row.id}`);
      setFormMode('edit');
      setFormId(full.id);
      setFormCode(full.code);
      setFormSujeto(full.sujeto ?? '');
      setFormText(full.text ?? '');
      setFormTareas(full.tareas ?? '');
      setFormMeta({
        tarea_actual: full.tarea_actual,
        tareas_completadas: full.tareas_completadas,
        fecha_creacion: full.fecha_creacion,
        pje: full.tareas_completadas_pje,
      });
      setFormCreated(null);
      setFormOpen(true);
    } catch (e) {
      alert(e.message);
    }
  };

  const cerrarForm = () => {
    setFormOpen(false);
    setFormCreated(null);
  };

  const addPickerTest = () => {
    if (!pickerId) return;
    const prefix = TAREA_PREFIX[pickerTipo];
    if (!prefix) return;
    const code = `${prefix}${pickerId}`;
    const list = parseTareasCsv(formTareas);
    if (!list.includes(code)) list.push(code);
    setFormTareas(joinTareas(list));
    setPickerId('');
  };

  const removeTareaChip = (code) => {
    setFormTareas(joinTareas(parseTareasCsv(formTareas).filter((c) => c !== code)));
  };

  const handleSave = async () => {
    setFormSaving(true);
    try {
      const body = {
        sujeto: formSujeto.trim(),
        text: formText,
        tareas: joinTareas(parseTareasCsv(formTareas)),
      };
      if (formMode === 'create') {
        const data = await adminPost('/api/admin/ensayos', body);
        setFormCreated(data);
        load();
      } else {
        await adminPut(`/api/admin/ensayos/${formId}`, body);
        cerrarForm();
        load();
      }
    } catch (e) {
      alert(e.message);
    } finally {
      setFormSaving(false);
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`¿Eliminar el ensayo ${row.code} (${row.sujeto})?`)) return;
    try {
      await adminDelete(`/api/admin/ensayos/${row.id}`);
      load();
    } catch (e) {
      alert(e.message);
    }
  };

  const tareasChips = parseTareasCsv(formTareas);
  const inputCls = 'w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-violet-500';

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="text-2xl font-bold text-slate-800">Ensayos</h1>
        <div className="flex gap-2 flex-wrap">
          <div className="flex gap-2">
            <input
              type="text"
              value={filtro}
              onChange={(e) => setFiltro(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && setQuery(filtro.trim())}
              placeholder="Buscar por sujeto o ID..."
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-violet-500"
            />
            <button
              onClick={() => setQuery(filtro.trim())}
              className="px-4 py-2 rounded-lg bg-slate-700 hover:bg-slate-800 text-white text-sm"
            >
              Buscar
            </button>
            {query && (
              <button
                onClick={() => { setFiltro(''); setQuery(''); }}
                className="px-3 py-2 rounded-lg border border-slate-300 text-slate-600 text-sm hover:bg-slate-50"
              >
                Limpiar
              </button>
            )}
          </div>
          <button
            onClick={handleExport}
            className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium"
          >
            Exportar CSV
          </button>
          <button
            onClick={openCreate}
            className="px-4 py-2 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-sm font-medium"
          >
            Nuevo ensayo
          </button>
        </div>
      </div>

      {loading ? (
        <p className="text-slate-500 text-sm">Cargando...</p>
      ) : (
        <div className="bg-white rounded-xl shadow-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th
                  onClick={toggleIdSort}
                  className="px-4 py-3 text-left font-medium text-slate-600 cursor-pointer select-none hover:text-slate-900"
                >
                  <span className="inline-flex items-center gap-1">
                    ID
                    <span className="text-[10px] leading-none">
                      {sortDir ? (sortDir === 'asc' ? '↑' : '↓') : <span className="text-slate-300">↕</span>}
                    </span>
                  </span>
                </th>
                <th className="px-4 py-3 text-left font-medium text-slate-600">Sujeto / Tareas</th>
                <th className="px-4 py-3 text-left font-medium text-slate-600">% Completado</th>
                <th className="px-4 py-3 text-left font-medium text-slate-600">Fecha</th>
                <th className="px-4 py-3 text-right font-medium text-slate-600">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {visibleRows.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-6 text-center text-slate-400">Sin registros</td></tr>
              )}
              {visibleRows.map((r) => (
                <tr key={r.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 text-slate-500 align-top">{r.id}</td>
                  <td className="px-4 py-3 align-top">
                    <div className="text-slate-800 font-medium">
                      {r.sujeto || 'Anónimo'}{' '}
                      <span className="font-mono font-normal text-slate-500">[{r.code}]</span>
                    </div>
                    <TareasBadges tareas={r.tareas} completadas={r.tareas_completadas} />
                  </td>
                  <td className="px-4 py-3 text-slate-700 align-top">{r.tareas_completadas_pje}%</td>
                  <td className="px-4 py-3 text-slate-500 text-xs align-top">{r.fecha_creacion}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap align-top">
                    <a
                      href={ensayoPublicUrl(r.code)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-emerald-600 hover:text-emerald-800 text-xs no-underline mr-2"
                    >
                      Abrir
                    </a>
                    <button
                      onClick={() => openDetail(r.id)}
                      className="text-violet-600 hover:text-violet-800 text-xs bg-transparent border-0 cursor-pointer mr-2"
                    >
                      Detalle
                    </button>
                    <button
                      onClick={() => openEdit(r)}
                      className="text-slate-600 hover:text-slate-900 text-xs bg-transparent border-0 cursor-pointer mr-2"
                    >
                      Editar
                    </button>
                    <button
                      onClick={() => handleDelete(r)}
                      className="text-red-500 hover:text-red-700 text-xs bg-transparent border-0 cursor-pointer"
                    >
                      Eliminar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal detalle */}
      {(detail || detailLoading) && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-slate-200 flex justify-between items-center">
              <h2 className="text-lg font-semibold text-slate-800">
                {detail ? `Ensayo ${detail.code} — ${detail.sujeto}` : 'Cargando...'}
              </h2>
              <button
                onClick={() => setDetail(null)}
                className="text-slate-400 hover:text-slate-700 text-xl bg-transparent border-0 cursor-pointer leading-none"
              >
                ×
              </button>
            </div>
            <div className="px-6 py-4">
              {detailLoading && <p className="text-slate-500 text-sm">Cargando...</p>}
              {detail && (
                <div className="mb-6 space-y-2 text-sm text-slate-700">
                  <p><span className="text-slate-500">Tareas:</span> {detail.tareas || '—'}</p>
                  <p><span className="text-slate-500">Completadas:</span> {detail.tareas_completadas || '—'}</p>
                  <p><span className="text-slate-500">%:</span> {detail.tareas_completadas_pje}%</p>
                  {detail.text && (
                    <p className="whitespace-pre-wrap"><span className="text-slate-500">Texto:</span> {detail.text}</p>
                  )}
                </div>
              )}
              {detail && detail.registros?.length === 0 && (
                <p className="text-slate-400 text-sm">Sin registros de resultados aún.</p>
              )}
              {detail && detail.registros?.map((reg) => (
                <div key={reg.id} className="mb-6">
                  <h3 className="font-semibold text-slate-700 mb-2 text-sm">
                    {reg.tipo} — {reg.fecha}
                  </h3>
                  {reg.resultados?.length > 0 ? (
                    <table className="w-full text-xs border-collapse">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-2 py-1 text-left text-slate-500">Pregunta</th>
                          <th className="px-2 py-1 text-left text-slate-500">Respuesta usuario</th>
                          <th className="px-2 py-1 text-left text-slate-500">Correcta</th>
                          <th className="px-2 py-1 text-left text-slate-500">Tiempo</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {reg.resultados.map((r, i) => (
                          <tr key={i}>
                            <td className="px-2 py-1 text-slate-700 max-w-[200px] truncate">{r.pregunta}</td>
                            <td className="px-2 py-1 text-slate-700">{r.respuestaUsuario}</td>
                            <td className={`px-2 py-1 font-medium ${r.correcta ? 'text-green-600' : 'text-red-500'}`}>
                              {r.correcta ? 'SI' : 'NO'}
                            </td>
                            <td className="px-2 py-1 text-slate-500">{r.tiempoRespuesta || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p className="text-slate-400 text-xs">Sin resultados detallados</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Modal create / edit */}
      {formOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-xl max-h-[90vh] overflow-y-auto p-6">
            <h2 className="text-lg font-semibold text-slate-800 mb-4">
              {formMode === 'create' ? 'Nuevo ensayo' : 'Editar ensayo'}
            </h2>

            {formCreated ? (
              <>
                <p className="text-sm text-slate-600 mb-2">
                  Ensayo creado para <strong>{formCreated.sujeto}</strong>. Código:
                </p>
                <p className="font-mono text-2xl text-center py-3 mb-2 rounded-lg bg-slate-100 text-slate-800 tracking-wider">
                  {formCreated.code}
                </p>
                <p className="text-xs text-slate-500 mb-1 text-center">Link para el participante:</p>
                <a
                  href={ensayoPublicUrl(formCreated.code)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-center text-sm text-violet-700 hover:underline break-all mb-4"
                >
                  {ensayoPublicUrl(formCreated.code)}
                </a>
                {formCreated.tareas && (
                  <p className="text-xs text-slate-500 mb-4 text-center break-all">
                    Tareas: {formCreated.tareas}
                  </p>
                )}
                <div className="flex justify-end gap-2">
                  <a
                    href={ensayoPublicUrl(formCreated.code)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium no-underline"
                  >
                    Abrir
                  </a>
                  <button
                    onClick={cerrarForm}
                    className="px-4 py-2 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-sm font-medium"
                  >
                    Cerrar
                  </button>
                </div>
              </>
            ) : (
              <div className="flex flex-col gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Código</label>
                  <input type="text" value={formCode} readOnly className={`${inputCls} bg-slate-50 text-slate-500`} />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Sujeto</label>
                  <input
                    type="text"
                    value={formSujeto}
                    onChange={(e) => setFormSujeto(e.target.value)}
                    placeholder="Nombre del sujeto"
                    className={inputCls}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Texto</label>
                  <textarea
                    value={formText}
                    onChange={(e) => setFormText(e.target.value)}
                    rows={4}
                    placeholder="Instrucciones / texto que ve el participante"
                    className={`${inputCls} resize-y`}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Tareas
                  </label>
                  <p className="text-xs text-slate-500 mb-2">
                    Códigos separados por coma, p. ej. <code className="bg-slate-100 px-1 rounded">LT82,DDI5,P3,R2,ASP1,MCP10</code>
                  </p>
                  <input
                    type="text"
                    value={formTareas}
                    onChange={(e) => setFormTareas(e.target.value)}
                    placeholder="LT82,DDI5,P3..."
                    className={`${inputCls} font-mono`}
                  />
                  {tareasChips.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {tareasChips.map((c) => (
                        <button
                          key={c}
                          type="button"
                          onClick={() => removeTareaChip(c)}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-violet-50 text-violet-700 text-xs border border-violet-200 hover:bg-red-50 hover:text-red-600 hover:border-red-200"
                          title="Quitar"
                        >
                          {c} ×
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                  <p className="text-xs font-medium text-slate-600 mb-2">Agregar test desde el catálogo</p>
                  <div className="flex flex-wrap gap-2 items-end">
                    <div className="flex-1 min-w-[140px]">
                      <label className="block text-[11px] text-slate-500 mb-0.5">Tipo</label>
                      <select
                        value={pickerTipo}
                        onChange={(e) => setPickerTipo(e.target.value)}
                        className={inputCls}
                      >
                        {TEST_TIPOS.map((t) => (
                          <option key={t.key} value={t.key}>{t.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="flex-[2] min-w-[180px]">
                      <label className="block text-[11px] text-slate-500 mb-0.5">Test</label>
                      <select
                        value={pickerId}
                        onChange={(e) => setPickerId(e.target.value)}
                        disabled={pickerLoading}
                        className={inputCls}
                      >
                        <option value="">{pickerLoading ? 'Cargando...' : '— Seleccionar —'}</option>
                        {pickerTests.map((t) => (
                          <option key={t.ID} value={t.ID}>
                            {TAREA_PREFIX[pickerTipo]}{t.ID} — {t.descripcion || 'Sin descripción'}
                          </option>
                        ))}
                      </select>
                    </div>
                    <button
                      type="button"
                      onClick={addPickerTest}
                      disabled={!pickerId}
                      className="px-3 py-2 rounded-lg bg-slate-700 hover:bg-slate-800 text-white text-sm disabled:opacity-40"
                    >
                      Agregar
                    </button>
                  </div>
                </div>

                {formMeta && (
                  <div className="text-xs text-slate-500 space-y-1 border-t border-slate-100 pt-3">
                    <p>Tarea actual: <span className="font-mono text-slate-700">{formMeta.tarea_actual || '—'}</span></p>
                    <p>Completadas: <span className="font-mono text-slate-700">{formMeta.tareas_completadas || '—'}</span></p>
                    <p>Fecha creación: {formMeta.fecha_creacion || '—'}</p>
                    <p>% completado: {formMeta.pje ?? 0}%</p>
                  </div>
                )}

                <div className="flex justify-end gap-3 pt-1">
                  <button
                    onClick={cerrarForm}
                    className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 text-sm hover:bg-slate-50"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={formSaving}
                    className="px-4 py-2 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-sm font-medium disabled:opacity-50"
                  >
                    {formSaving ? 'Guardando...' : (formMode === 'create' ? 'Crear' : 'Guardar')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
